import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { PanelLeftClose, PanelLeftOpen, Search, Bell } from "lucide-react";
import { menuVisivel, type ItemMenu } from "./menu.js";
import { t } from "./i18n.js";
import { useEu, usePode } from "./Sessao.js";
import { useQuery } from "@tanstack/react-query";
import { trpc } from "../lib/trpc.js";
import { Marca, Simbolo } from "@pessoas/ui";
import { MenuUsuario } from "./MenuUsuario.js";
import { SeletorDeEmpresa } from "./SeletorDeEmpresa.js";

const CHAVE_LARGURA = "eop:menu:largura";
const MIN = 216, MAX = 360, PADRAO = 252, RECOLHIDO = 64;

/**
 * Item ativo por PREFIXO DE SEGMENTO, e não por `startsWith` cru.
 *
 * `startsWith` acende /ponto quando a rota é /pontos-de-venda, porque compara
 * texto e não caminho. E a raiz só casa exata, senão fica acesa em tudo.
 */
export function estaAtivo(rota: string, destino: string): boolean {
  if (destino === "/") return rota === "/";
  return rota === destino || rota.startsWith(destino + "/");
}

export function Shell({ children }: { children: React.ReactNode }) {
  const [largura, setLargura] = useState(PADRAO);
  const [recolhido, setRecolhido] = useState(false);
  const rota = useRouterState({ select: (s) => s.location.pathname });
  const eu = useEu();
  const busca = useRef<HTMLInputElement>(null);

  const itens = useMemo(() => menuVisivel(eu.permissoes), [eu.permissoes]);

  // MESMA chave do painel, de propósito: o TanStack Query deduplica, então o
  // menu e a tela dividem uma requisição só em vez de duas.
  const competencia = new Date().toISOString().slice(0, 7);
  const podeVerContadores = usePode("relatorio.ler");
  const resumo = useQuery({
    queryKey: ["painel", competencia],
    queryFn: () => trpc.painel.resumo.query({ competencia, diasDeAlerta: 30 }),
    enabled: podeVerContadores,
    // Contador de pendência não precisa ser ao vivo, precisa ser recente. Um
    // minuto evita refazer a consulta a cada troca de tela.
    staleTime: 60_000,
    retry: false,
  });

  const contarAlertas = (chaves: readonly string[] | undefined): number => {
    if (!chaves || !resumo.data) return 0;
    return chaves.reduce((t, c) => t + (resumo.data.alertas[c]?.quantidade ?? 0), 0);
  };

  useEffect(() => {
    const salvo = Number(localStorage.getItem(CHAVE_LARGURA));
    if (salvo >= MIN && salvo <= MAX) setLargura(salvo);
    setRecolhido(localStorage.getItem("eop:menu:recolhido") === "1");
  }, []);

  // Ctrl+K e Cmd+K caem na busca. É o atalho que quem usa sistema o dia
  // inteiro procura antes de olhar para o menu.
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        busca.current?.focus();
        busca.current?.select();
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);

  const recolher = (v: boolean) => {
    setRecolhido(v);
    localStorage.setItem("eop:menu:recolhido", v ? "1" : "0");
  };

  const redimensionar = (e: React.PointerEvent) => {
    e.preventDefault();
    const move = (ev: PointerEvent) =>
      setLargura(Math.min(MAX, Math.max(MIN, ev.clientX)));
    const solta = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", solta);
      setLargura((l) => { localStorage.setItem(CHAVE_LARGURA, String(l)); return l; });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", solta);
  };

  return (
    <div className="eop eop-shell">
      <nav
        aria-label={t("app.nome")}
        className="eop-lateral"
        style={{ width: recolhido ? RECOLHIDO : largura }}
        data-recolhido={recolhido || undefined}
      >
        {/* Duas linhas, e não uma: a MARCA diz de que produto é a tela, a
            EMPRESA diz de qual CNPJ é o número. Empilhar as duas no mesmo
            controle faria a marca da casa virar rótulo de tenant. */}
        <div className="eop-lateral-topo">
          {recolhido ? <Simbolo tamanho={22} /> : <Marca tamanho={24} />}
        </div>
        <div className="eop-lateral-empresa">
          <SeletorDeEmpresa recolhido={recolhido} />
        </div>

        <div className="eop-lateral-rolagem">
          <ul className="eop-menu">
            {itens.map((e, i) =>
              e.tipo === "cabecalho" ? (
                <li key={`h${i}`} className="eop-menu-grupo">
                  {/* Recolhido, o título vira um risco: texto de 9px em 64px de
                      largura não é leitura, é sujeira. */}
                  {recolhido
                    ? <span aria-hidden className="eop-menu-risco" />
                    : <span className="eyebrow">{t(e.chave)}</span>}
                </li>
              ) : (
                <li key={e.para}>
                  <ItemDeMenu
                    item={e} ativo={estaAtivo(rota, e.para)} recolhido={recolhido}
                    conta={contarAlertas(e.contador)}
                  />
                </li>
              ))}
          </ul>
        </div>

        <div className="eop-lateral-rodape">
          <MenuUsuario recolhido={recolhido} />
        </div>

        {!recolhido && (
          <div
            role="separator" aria-orientation="vertical"
            aria-label={t("shell.redimensionar")}
            onPointerDown={redimensionar}
            className="eop-puxador"
          />
        )}
      </nav>

      <div className="eop-conteudo">
        <header className="eop-topo">
          <button
            type="button" className="botao-icone"
            aria-label={recolhido ? t("shell.expandir") : t("shell.recolher")}
            onClick={() => recolher(!recolhido)}
          >
            {recolhido
              ? <PanelLeftOpen aria-hidden size={16} />
              : <PanelLeftClose aria-hidden size={16} />}
          </button>

          <div className="eop-busca">
            <Search aria-hidden size={15} className="eop-busca-icone" />
            <input
              ref={busca} className="campo eop-busca-campo"
              placeholder={t("app.buscar")} aria-label={t("app.buscar")}
            />
            <kbd aria-hidden className="eop-atalho">⌘K</kbd>
          </div>

          <div className="flex-1" />

          <button type="button" className="botao-icone" aria-label={t("app.avisos")}>
            <Bell aria-hidden size={16} />
          </button>
        </header>

        {/* min-w-0 na coluna: sem isso, tabela larga estoura o layout inteiro
            em vez de rolar dentro do próprio contêiner. */}
        <main className="eop-principal">{children}</main>
      </div>
    </div>
  );
}

function ItemDeMenu(
  { item, ativo, recolhido, conta }:
  { item: ItemMenu; ativo: boolean; recolhido: boolean; conta: number },
) {
  const Icone = item.icone;
  const rotulo = t(item.chave);
  return (
    <Link
      to={item.para}
      className="item-menu"
      data-ativo={ativo}
      aria-current={ativo ? "page" : undefined}
      // Recolhido, o title é a ÚNICA forma de saber o que é o ícone. Com a
      // contagem junto, senão o número aparece sem dizer do que ele é.
      title={recolhido ? (conta > 0 ? `${rotulo} (${conta})` : rotulo) : undefined}
    >
      <Icone aria-hidden size={16} className="item-menu-icone" />
      {!recolhido && <span className="truncate">{rotulo}</span>}
      {conta > 0 && (
        <span className="item-menu-conta" data-severo={item.contadorSevero || undefined}>
          {/* Acima de 99 o número estoura a pílula e a precisão deixa de
              importar: o que muda a ação é "muitos", não "127". */}
          {conta > 99 ? "99+" : conta}
        </span>
      )}
    </Link>
  );
}
